import { Button, DaySchedule, GhostButton, Modal, Spinner } from '@mgh-app/component-library';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
  useGetTemperatureValuesQuery,
  useSetTemperatureScheduleMutation,
} from 'services/ThingsService';
import {
  alignItems,
  display,
  fontSize,
  gridTemplateRows,
  justifyContent,
  margin,
  textColor,
  twCls,
} from 'style';
import { ScheduleDetails } from './ScheduleDetails';

type Schedule = { [key: string]: { setPoint: number } };

const calcTime = (day: string, hour: number, minutes: number) =>
  (day === '0-4' ? 0 : 1) * 24 * 60 + hour * 60 + minutes;

const mapSchedule = (schedule: Schedule) =>
  Object.entries(schedule)
    .map(([scheduleKey, schedule]) => ({
      scheduleKey,
      hour: Number(scheduleKey.split(' ')[1]),
      minutes: Number(scheduleKey.split(' ')[0]),
      day: scheduleKey.split(' ')[2],
      setPoint: schedule.setPoint,
    }))
    .map((schedule) => ({
      ...schedule,
      startTime: calcTime(schedule.day, schedule.hour, schedule.minutes),
    }))
    .sort((a, b) => a.startTime - b.startTime)
    .map((schedule, index, array) => ({
      ...schedule,
      endTime: array[index + 1]?.startTime || 24 * 60,
      nextScheduleKey: array[index + 1]?.scheduleKey,
    }));

export const SchedulePage = () => {
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [currentScheduleKey, setCurrentScheduleKey] = useState<string>('');
  const [dayPart, setDayPart] = useState<string>('0-4');
  const [schedule, setSchedule] = useState<Schedule>();
  const [setTemperatureSchedule] = useSetTemperatureScheduleMutation();
  const { temperatureThingId = '' } = useParams();
  const { data: temperatureData, isLoading: isLoadingTemperature } =
    useGetTemperatureValuesQuery(temperatureThingId);

  const mappedSchedule = schedule && mapSchedule(schedule);

  useEffect(() => {
    if (temperatureData?.schedule && Object.keys(temperatureData?.schedule).length > 0) {
      setSchedule(temperatureData?.schedule);
    } else if (temperatureData) {
      console.log(temperatureData);
      setSchedule({
        ['0 0 0-4']: { setPoint: temperatureData.setPoint },
        ['0 0 5-6']: { setPoint: temperatureData.setPoint },
      });
    }
  }, [temperatureData, temperatureData?.schedule]);

  const workWeekSchedule =
    schedule &&
    Object.fromEntries(Object.entries(schedule).filter(([key]) => key.split(' ')[2] === '0-4'));
  const weekendSchedule =
    schedule &&
    Object.fromEntries(Object.entries(schedule).filter(([key]) => key.split(' ')[2] === '5-6'));

  const onItemClick = (key: string) => {
    setCurrentScheduleKey(key);
    setOpenDetails(true);
  };

  const onWorkWeekItemClick = (key: string) => {
    setDayPart('0-4');
    onItemClick(key);
  };
  const onWeekendItemClick = (key: string) => {
    setDayPart('5-6');
    onItemClick(key);
  };

  const onClose = () => setOpenDetails(false);

  const onRemove = (scheduleKey?: string) => {
    if (scheduleKey && schedule) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [scheduleKey]: _, ...newSchedule } = schedule;
      setSchedule(newSchedule);
    }
    onClose();
  };

  const onSave = (
    startTime: DateTime,
    endTime: DateTime,
    setPoint: number,
    scheduleKey?: string,
  ) => {
    if (schedule) {
      const newScheduleKey = `${startTime.minute} ${startTime.hour} ${dayPart}`;
      const startTimeNumber = calcTime(dayPart, startTime.hour, startTime.minute);
      const endTimeNumber =
        endTime.hour === 0 && endTime.minute === 0
          ? 24 * 60
          : calcTime(dayPart, endTime.hour, endTime.minute);
      const newSchedule = {
        ...schedule,
      };

      if (scheduleKey) {
        delete newSchedule[scheduleKey];
      }

      const sortedSchedule = mapSchedule(schedule).filter((schedule) => schedule.day === dayPart);
      const overlappingSchedule = sortedSchedule.filter(
        (schedule) => startTimeNumber <= schedule.startTime && schedule.endTime <= endTimeNumber,
      );
      const nextSchedule = sortedSchedule
        .filter(
          (schedule) =>
            endTimeNumber <= schedule.startTime ||
            (startTimeNumber <= schedule.startTime &&
              schedule.startTime <= endTimeNumber &&
              endTimeNumber < schedule.endTime),
        )
        .sort((a, b) => a.startTime - b.startTime)[0];

      if (overlappingSchedule?.length > 0) {
        overlappingSchedule.forEach((scheduleItem) => {
          delete newSchedule[scheduleItem.scheduleKey];
        });
      }

      if (nextSchedule) {
        delete newSchedule[nextSchedule.scheduleKey];
        const updateNextScheduleKey = `${endTime.minute} ${endTime.hour} ${dayPart}`;
        newSchedule[updateNextScheduleKey] = { setPoint: nextSchedule.setPoint };
      }

      // case: 00:00 - 24:00 and we add 14:00 - 15:00 then we need to split into
      // 3 slots: 00:00-14:00, 14:00-15:00, 15:00-24:00
      const containingSchedule = sortedSchedule
        .filter(
          (schedule) => schedule.startTime < startTimeNumber && endTimeNumber < schedule.endTime,
        )
        .sort((a, b) => a.startTime - b.startTime)[0];

      if (containingSchedule) {
        const additionalScheduleKey = `${endTime.minute} ${endTime.hour} ${dayPart}`;
        newSchedule[additionalScheduleKey] = { setPoint: containingSchedule.setPoint };
      }

      newSchedule[newScheduleKey] = { setPoint };

      setSchedule(newSchedule);
    }
    onClose();
  };

  const onConfirmSchedule = async () => {
    schedule && (await setTemperatureSchedule({ thingId: temperatureThingId, schedule }));
  };

  return (
    <>
      {isLoadingTemperature ? (
        <Spinner />
      ) : (
        <div
          className={twCls(margin('mt-16'), display('grid'), gridTemplateRows('grid-rows-4-min'))}
        >
          <div
            className={twCls(
              fontSize('text-sm'),
              textColor('text-grey-900'),
              display('flex'),
              justifyContent('justify-between'),
              alignItems('items-center'),
            )}
          >
            <div>Monday to Friday</div>
            <GhostButton
              className={twCls(fontSize('text-sm'))}
              onClick={() => onWorkWeekItemClick('')}
            >
              + Add / - Remove
            </GhostButton>
          </div>
          <DaySchedule
            schedule={workWeekSchedule || {}}
            onScheduleItemClick={onWorkWeekItemClick}
          />
          <div
            className={twCls(
              margin('mt-11'),
              fontSize('text-sm'),
              textColor('text-grey-900'),
              display('flex'),
              justifyContent('justify-between'),
              alignItems('items-center'),
            )}
          >
            <div>Saturday & Sunday</div>
            <GhostButton
              className={twCls(fontSize('text-sm'))}
              onClick={() => onWeekendItemClick('')}
            >
              + Add / - Remove
            </GhostButton>
          </div>
          <DaySchedule schedule={weekendSchedule || {}} onScheduleItemClick={onWeekendItemClick} />
          <Button
            onClick={onConfirmSchedule}
            disabled={!temperatureData || !schedule}
            className={twCls(margin('mt-16'))}
          >
            Confirm Schedule
          </Button>
          <Modal open={openDetails} onClose={onClose} fullWidth>
            <ScheduleDetails
              scheduleKey={currentScheduleKey}
              nextScheduleKey={
                mappedSchedule?.find((schedule) => schedule.scheduleKey === currentScheduleKey)
                  ?.nextScheduleKey
              }
              scheduleItem={schedule?.[currentScheduleKey] ?? { setPoint: 19.0 }}
              onClose={onClose}
              onRemove={onRemove}
              onSave={onSave}
            />
          </Modal>
        </div>
      )}
    </>
  );
};
